"use client";

import Image from "next/image";
import Link from "next/link";
import { notFound } from "next/navigation";
import React, { useEffect, useState } from "react";

import { Post } from "@/app/utils/interface";
import { client } from "@/sanity/lib/client";

const POSTS_PER_PAGE = 6;

// Function to get YouTube thumbnail URL
const getYouTubeThumbnail = (videoId: string) => {
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
};

async function getPosts() {
  const query = `*[_type == "post" && "patient-testimonies" in categories[]->slug.current] | order(publishedAt desc) {
    _id,
    title,
    slug,
    publishedAt,
    excerpt,
    cloudinaryImage,
    youtubeVideo,
    "categories": categories[]-> {
      _id,
      title,
      slug
    }
  }`;

  const posts = await client.fetch(query);
  return posts;
}

const Page = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const posts = await getPosts();
        setPosts(posts);
      } catch (error) {
        console.error("Error fetching data:", error);
        notFound();
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="size-32 animate-spin rounded-full border-y-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!posts) {
    notFound();
  }

  return (
    <section className="text-gray-700">
      <div className="flex flex-col items-center justify-center gap-5">
        <div className="mx-auto w-full bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 p-5">
          <div className="mx-auto flex max-w-4xl flex-col items-center justify-center gap-5">
            <h1 className="relative z-20 animate-slideInFromLeft text-center text-2xl font-bold leading-none text-violet-950 md:text-left lg:text-3xl xl:text-4xl">
              Patient Testimonies
            </h1>
            <p className="text-center text-lg text-white">
              Hear from our patients about their experiences and success stories
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((post: Post) => (
            <article
              key={post._id}
              className="group relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:shadow-lg"
            >
              {(post.cloudinaryImage?.secure_url || post.youtubeVideo) && (
                <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                  <Image
                    src={
                      post.cloudinaryImage?.secure_url ||
                      getYouTubeThumbnail(post.youtubeVideo || "")
                    }
                    alt={post.title}
                    width={500}
                    height={300}
                    className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => {
                      // Fallback to a lower quality thumbnail if maxresdefault fails
                      if (post.youtubeVideo) {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://img.youtube.com/vi/${post.youtubeVideo}/hqdefault.jpg`;
                      }
                    }}
                  />
                </div>
              )}
              <div className="flex flex-1 flex-col p-6">
                <div className="flex-1">
                  <div className="mb-2 flex flex-wrap gap-2">
                    {post.categories?.map(
                      (category: {
                        _id: string;
                        title: string;
                        slug: { current: string };
                      }) => (
                        <Link
                          key={category._id}
                          href={`/${category.slug.current}`}
                          className="text-xs font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          {category.title}
                        </Link>
                      )
                    )}
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    <Link
                      href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
                      className="hover:text-indigo-600"
                    >
                      {post.title}
                    </Link>
                  </h2>
                  <p className="mt-3 text-base text-gray-500">{post.excerpt}</p>
                </div>
                <div className="mt-4 flex items-center justify-end">
                  <Link
                    href={`/${post.categories[0]?.slug.current}/${post.slug.current}`}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    Read more →
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Page;
